import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import api from "../api.js";
import CenterWrapper from "../components/CenterWrapper.jsx";
import FileUpload from "./Setup/components/FileUpload.jsx";
import {
  EnvelopeIcon,
  ExclamationCircleIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";
import SectionHeader from "../components/SectionHeader";
import Input from "../components/Input";
import PrimaryButton from "../components/PrimaryButton";
import SecondaryButton from "../components/SecondaryButton";

const Profile = () => {
  const [userProfile, setUserProfile] = useState({});
  const [feedback, setFeedback] = useState({});
  const [passwordFeedback, setPasswordFeedback] = useState({});

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      first_name: "",
      last_name: "",
      password: "",
      passwordConfirmation: "",
      phone: "",
    },
  });

  const fetchProfile = async () => {
    try {
      const response = await api.get(`/api/user`);
      const userProfile = response.data;
      setUserProfile(userProfile);

      setValue("first_name", userProfile.first_name);
      setValue("last_name", userProfile.last_name);
      setValue("email", userProfile.email);
      setValue("phone", userProfile.phone);
    } catch (err) {
      setFeedback({
        status: "error",
        msg: JSON.stringify(err), // TODO: make friendlier
      });
    }
  };

  const updateProfile = async data => {
    try {
      const response = await api.put(`/api/user`, data);

      setFeedback({
        status: "success",
        msg: "Profile updated successfully",
      });

      setUserProfile(response.data);
    } catch (err) {
      setFeedback({
        status: "error",
        msg: err.response.data.detail,
      });
    }
  };

  const updatePassword = async data => {
    try {
      await api.put(`/api/user/password`, data);

      setPasswordFeedback({
        status: "success",
        msg: "Password updated successfully",
      });

      reset({
        password: "",
        passwordConfirmation: "",
      });
    } catch (err) {
      setPasswordFeedback({
        status: "error",
        msg: err.response.data.detail,
      });
    }
  };

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async data => {
    updateProfile({
      first_name: data.first_name,
      last_name: data.last_name,
      phone: data.phone,
    });

    setTimeout(() => setFeedback({}), 10000);
  };

  const onPasswordSubmit = async data => {
    updatePassword({
      password: data.password,
    });

    setTimeout(() => setPasswordFeedback({}), 10000);
  };

  const uploadProfileFile = async file => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      await api.post(`/api/upload_profile_picture`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (err) {
      alert(err.message);
    }
  };

  const tabs = [
    {
      key: "Profile",
      name: "Profile",
    },
  ];

  const activeTab = tabs[0].key;

  return (
    <CenterWrapper>
      <div>
        <SectionHeader
          title="Profile Settings"
          description="Update your personal settings."
        />

        <nav className="flex space-x-1 sm:space-x-4 border-b-2 text-xs sm:text-base my-8">
          {tabs.map((tab, index) => (
            <button
              key={index}
              className={`font-semibold px-3 py-2 rounded-md ${activeTab === tab.key ? "text-primary border-b-2 border-primary rounded-none" : "text-gray-500"}`}
            >
              {tab.name}
            </button>
          ))}
        </nav>

        <div className="flex items-start gap-6 justify-between max-sm:flex-col">
          <div className="sm:max-w-sm">
            <SectionHeader
              title="Basic Information"
              description="Update your name and profile photo. Your first name and profile
              photo will be displayed when messaging your customers."
            />
          </div>

          <div className="w-full sm:max-w-xl">
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="border p-5 rounded-xl"
            >
              {feedback.status && (
                <div className="mb-4">
                  <div
                    className={`flex items-center p-4 rounded-lg ${
                      feedback.status === "success"
                        ? "text-green-800 bg-green-50"
                        : "text-red-800 bg-red-50"
                    }`}
                    role="alert"
                  >
                    <svg
                      className="flex-shrink-0 w-4 h-4"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z" />
                    </svg>
                    <span className="sr-only">Info</span>
                    <div className="ms-3 text-sm font-medium">
                      {feedback["msg"]}
                    </div>
                    <button
                      onClick={() => setFeedback({})}
                      className={`ms-auto -mx-1.5 -my-1.5 rounded-lg focus:ring-2 p-1.5 inline-flex items-center justify-center h-8 w-8 dark:bg-gray-800 ${
                        feedback["status"] === "success"
                          ? "bg-green-50 text-green-500 hover:bg-green-200 focus:ring-green-400"
                          : "bg-red-50 text-red-500 hover:bg-red-200 focus:ring-red-400"
                      }`}
                      aria-label="Close"
                    >
                      <span className="sr-only">Close</span>
                      <svg
                        className="w-3 h-3"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 14 14"
                      >
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="m1 1 12 12m0-12L1 13"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-6">
                <div className="sm:col-span-3">
                  <label
                    htmlFor="first_name"
                    className="block text-sm font-semibold leading-6 text-gray-900 mb-2"
                  >
                    First Name
                  </label>
                  <Input
                    name="first_name"
                    register={register}
                    error={errors?.first_name?.message}
                  />
                </div>

                <div className="sm:col-span-3">
                  <label
                    htmlFor="last_name"
                    className="block text-sm font-semibold leading-6 text-gray-900 mb-2"
                  >
                    Last Name
                  </label>
                  <Input
                    register={register}
                    error={errors?.last_name?.message}
                    name="last_name"
                  />
                </div>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold leading-6 text-gray-900 mb-2"
                  >
                    Email
                  </label>

                  <Input
                    register={register}
                    name="email"
                    disabled
                    startIcon={
                      <EnvelopeIcon aria-hidden="true" className="size-6" />
                    }
                  />
                </div>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="phone"
                    className="block text-sm font-semibold leading-6 text-gray-900 mb-2"
                  >
                    Phone
                  </label>

                  <Input
                    error={errors?.phone?.message}
                    register={register}
                    name="phone"
                    startIcon={
                      <PhoneIcon aria-hidden="true" className="size-6" />
                    }
                  />
                </div>

                <div className="sm:col-span-6">
                  <FileUpload
                    subtitle="Allowed formats: SVG, PNG, JPG, GIF"
                    maxFileSizeMB={5}
                    onUpload={uploadProfileFile}
                    currentValue={userProfile.picture_url}
                  />
                </div>
              </div>

              <div className="mt-8 flex justify-end gap-4">
                <div>
                  <SecondaryButton type="button" onClick={() => reset()}>
                    Cancel
                  </SecondaryButton>
                </div>
                <div>
                  <PrimaryButton>Save changes</PrimaryButton>
                </div>
              </div>
            </form>
          </div>
        </div>

        <div className="flex items-start gap-6 justify-between max-sm:flex-col mt-8">
          <div className="sm:max-w-sm">
            <SectionHeader
              title="Password"
              description="Please enter your current password to change your password."
            />
          </div>

          <div className="w-full border p-5 rounded-xl sm:max-w-xl">
            {userProfile.provider !== "email" && (
              <div className="w-full text-sm flex">
                <ExclamationCircleIcon
                  aria-hidden="true"
                  className={`h-8 w-8 text-primary mr-2`}
                />
                You signed up with your Gmail or Google Business Account.
                Resetting your password is not available.
              </div>
            )}

            {userProfile.provider === "email" && (
              <form
                onSubmit={handleSubmit(onPasswordSubmit)}
                className="md:col-span-2"
              >
                <div>
                  <div className="w-full">
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium leading-6 text-gray-900 mb-2"
                    >
                      New password
                    </label>
                    <Input
                      register={register}
                      type="password"
                      name="password"
                      placeholder="**************"
                      error={errors?.password?.message}
                    />
                  </div>

                  <div className="w-full">
                    <label
                      htmlFor="passwordConfirmation"
                      className="mt-6 block text-sm font-medium leading-6 text-gray-900 mb-2"
                    >
                      Confirm password
                    </label>
                    <Input
                      register={register}
                      type="password"
                      placeholder="**************"
                      name="passwordConfirmation"
                      error={errors?.passwordConfirmation?.message}
                    />
                  </div>
                </div>

                {passwordFeedback.status && (
                  <div className="mt-4">
                    <div
                      className={`flex items-center p-4 rounded-lg ${
                        passwordFeedback.status === "success"
                          ? "text-green-800 bg-green-50"
                          : "text-red-800 bg-red-50"
                      }`}
                      role="alert"
                    >
                      <svg
                        className="flex-shrink-0 w-4 h-4"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z" />
                      </svg>
                      <span className="sr-only">Info</span>
                      <div className="ms-3 text-sm font-medium">
                        {passwordFeedback["msg"]}
                      </div>
                      <button
                        onClick={() => setPasswordFeedback({})}
                        className={`ms-auto -mx-1.5 -my-1.5 rounded-lg focus:ring-2 p-1.5 inline-flex items-center justify-center h-8 w-8 dark:bg-gray-800 ${
                          passwordFeedback["status"] === "success"
                            ? "bg-green-50 text-green-500 hover:bg-green-200 focus:ring-green-400"
                            : "bg-red-50 text-red-500 hover:bg-red-200 focus:ring-red-400"
                        }`}
                        aria-label="Close"
                      >
                        <span className="sr-only">Close</span>
                        <svg
                          className="w-3 h-3"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 14 14"
                        >
                          <path
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="m1 1 12 12m0-12L1 13"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                <div className="mt-8 flex justify-end gap-4">
                  <div>
                    <SecondaryButton type="button" onClick={() => reset()}>
                      Cancel
                    </SecondaryButton>
                  </div>
                  <div>
                    <PrimaryButton>Save changes</PrimaryButton>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </CenterWrapper>
  );
};

export default Profile;
